import React, { useState } from 'react';
import { userKey } from '../utils/storageHelper.js';
import { useSystemProfile } from '../utils/systemProfile.js';
import { incrementDocCount } from '../utils/docCounter.js';
import { saveDocToHistory } from '../utils/docHistory.js';
import { callLLM } from './llmService';
import { supabase } from '../lib/supabaseClient.js';
import classifierData from '../data/eu-ai-act-classifier.json';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { getDeadlineStatus } from '../data/complianceDeadlines.js';
import { EUFlagIcon } from './EUFlagIcon.jsx';


const DEADLINE_AUG2   = '2026-08-02';   // Article 50 transparency — chatbot, AI content labelling
// eslint-disable-next-line no-unused-vars
const DEADLINE_DEC2_2026 = '2026-12-02'; // Article 50(2) watermarking grace period ends
const DEADLINE_DEC2   = '2027-12-02';   // Annex III high-risk standalone systems (Digital Omnibus)
// eslint-disable-next-line no-unused-vars
const DEADLINE_AUG28  = '2028-08-02';   // Annex I high-risk embedded in regulated products
// eslint-disable-next-line no-unused-vars
const DEADLINE_DEC30  = '2030-12-31';   // Large-scale IT systems final deadline

// EU AI Act full deadline reference table — used in reports and PDF.
// Dates are fixed legal facts, but whether each one is PAST/ACTIVE/UPCOMING is NOT — that
// depends on today's date and must never be hardcoded (QAIS standard: status is always computed
// at render time, same pattern as getDeadlineStatus() in complianceDeadlines.js). `iso` drives
// the live computation below via getEuTimelineStatus(); do not add a static `status` field here.
const EU_DEADLINES = [
  { date: 'Feb 2, 2025',  iso: '2025-02-02', label: 'Prohibited practices (Article 5)',                    detail: 'Already in force — banned AI practices enforceable now' },
  { date: 'Aug 2, 2025',  iso: '2025-08-02', label: 'GPAI model obligations',                              detail: 'General-purpose AI model transparency and documentation' },
  { date: 'Aug 2, 2026',  iso: '2026-08-02', label: 'Article 50 transparency obligations',                 detail: 'Chatbot disclosure, AI content labelling — applies to ALL companies in scope' },
  { date: 'Dec 2, 2026',  iso: '2026-12-02', label: 'Article 50(2) watermarking grace period ends',        detail: 'Machine-readable marking of AI-generated content mandatory' },
  { date: 'Dec 2, 2027',  iso: '2027-12-02', label: 'Annex III high-risk systems (standalone)',             detail: 'Full high-risk obligations: conformity assessment, CE marking, EU database registration' },
  { date: 'Aug 2, 2028',  iso: '2028-08-02', label: 'Annex I high-risk (embedded in regulated products)',   detail: 'AI in medical devices, machinery, vehicles — full high-risk compliance' },
  { date: 'Dec 31, 2030', iso: '2030-12-31', label: 'Large-scale IT systems',                                detail: 'Final deadline for AI in large-scale EU justice and security IT systems' },
];

// Maps the 4-tier getDeadlineStatus() result (in-force/urgent/approaching/upcoming) onto the
// 3-tier PAST/ACTIVE/UPCOMING labels this timeline's UI/PDF already render — computed fresh on
// every call from today's date, so a deadline that passes automatically flips to PAST with no
// code change needed.
function getEuTimelineStatus(isoDate) {
  const { status } = getDeadlineStatus(isoDate);
  if (status === 'in-force') return 'PAST';
  if (status === 'urgent' || status === 'approaching') return 'ACTIVE';
  return 'UPCOMING';
}

function UrgencyBadge({ dateStr, label }) {
  const s = getDeadlineStatus(dateStr);

  if (s.status === 'in-force') {
    return (
      <span style={{
        backgroundColor: '#f1f5f9', color: '#64748b', padding: '6px 14px',
        borderRadius: '20px', fontSize: '13px', fontWeight: '800',
        border: '2px solid #94a3b8', display: 'inline-flex', alignItems: 'center', gap: '6px',
      }}>
        ✅ In force since {label}
      </span>
    );
  }

  const color = s.status === 'urgent' ? '#ef4444' : s.status === 'approaching' ? '#f59e0b' : '#3b82f6';
  const bg = s.status === 'urgent' ? '#fef2f2' : s.status === 'approaching' ? '#fffbeb' : '#eff6ff';
  return (
    <span style={{
      backgroundColor: bg, color, padding: '6px 14px', borderRadius: '20px',
      fontSize: '13px', fontWeight: '800', border: `2px solid ${color}`,
      animation: s.status === 'urgent' ? 'urgency-blink 1.2s ease-in-out infinite' : 'none',
      display: 'inline-flex', alignItems: 'center', gap: '6px',
    }}>
      ⏰ <strong>{s.daysUntil} days</strong> — {label}
    </span>
  );
}

// ── YesNo — reusable Yes/No button pair for nexus gate ───────────────────
// QAIS standard: defined outside parent component to prevent remount on state change
function YesNo({ id, label, sublabel, article, answers, setAnswers }) {
  return (
    <div style={{
      backgroundColor: 'white', border: '2px solid',
      borderColor: answers[id] === 'yes' ? '#ef4444' :
                   answers[id] === 'no'  ? '#10b981' : '#e2e8f0',
      borderRadius: '12px', padding: '20px', marginBottom: '14px',
      transition: 'border-color 0.15s'
    }}>
      <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start', marginBottom: '14px' }}>
        <span style={{ fontSize: '12px', fontWeight: '700', padding: '2px 8px', borderRadius: '4px',
          backgroundColor: '#eff6ff', color: '#1e40af', flexShrink: 0, marginTop: '2px' }}>
          {article}
        </span>
        <div>
          <p style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px', lineHeight: '1.4' }}>{label}</p>
          {sublabel && <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: '1.5' }}>{sublabel}</p>}
        </div>
      </div>
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={() => setAnswers(prev => ({ ...prev, [id]: 'yes' }))}
          style={{
            flex: 1, padding: '10px', borderRadius: '8px', fontSize: '14px',
            fontWeight: '700', cursor: 'pointer', border: '2px solid',
            backgroundColor: answers[id] === 'yes' ? '#ef4444' : 'white',
            borderColor: answers[id] === 'yes' ? '#ef4444' : '#e2e8f0',
            color: answers[id] === 'yes' ? 'white' : '#334155',
            transition: 'all 0.15s'
          }}
        >✓ Yes</button>
        <button
          onClick={() => setAnswers(prev => ({ ...prev, [id]: 'no' }))}
          style={{
            flex: 1, padding: '10px', borderRadius: '8px', fontSize: '14px',
            fontWeight: '700', cursor: 'pointer', border: '2px solid',
            backgroundColor: answers[id] === 'no' ? '#10b981' : 'white',
            borderColor: answers[id] === 'no' ? '#10b981' : '#e2e8f0',
            color: answers[id] === 'no' ? 'white' : '#334155',
            transition: 'all 0.15s'
          }}
        >✗ No</button>
      </div>
    </div>
  );
}

function EUAIActClassifier({ onNavigate }) {
  const _profile = useSystemProfile(); // Must be first — used in useState initializers below

  // ── Nexus gate state ─────────────────────────────────────────────────────
  // eslint-disable-next-line no-unused-vars
  const [nexusAnswers, setNexusAnswers] = useState(() => {
    try {
      const saved = localStorage.getItem(userKey('qais_eu_nexus_answers'));
      return saved ? JSON.parse(saved) : { q1: null, q2: null, q3: null, q4: null, q5: null, q6: null };
    } catch { return { q1: null, q2: null, q3: null, q4: null, q5: null, q6: null }; }
  });
  const [nexusResult, setNexusResult] = useState(() => {
    try {
      const saved = localStorage.getItem(userKey('qais_eu_nexus_result'));
      return saved || null;
    } catch { return null; }
  });

  // ── Determine initial screen ──────────────────────────────────────────────
  // If nexus already answered → go to intro (or rehydrate results)
  // If not answered → start at nexus_check
  // eslint-disable-next-line no-unused-vars
  const nexusDone = !!nexusResult;

  const [screen, setScreen] = useState(() => {
    try {
      // Check for saved full result first
      const savedResult = localStorage.getItem(userKey('qais_eu_classifier_result'));
      if (savedResult) {
        const parsed = JSON.parse(savedResult);
        if (parsed.classification && parsed.reasoning) return 'results';
      }
      // Check for advisory result
      const savedAdvisory = localStorage.getItem(userKey('qais_eu_advisory_result'));
      if (savedAdvisory) return 'advisory_report';
      // Check for saved skip
      const skipped = localStorage.getItem(userKey('qais_eu_classifier_skipped'));
      if (skipped) return 'skipped';
      // Check nexus done
      const nexusRes = localStorage.getItem(userKey('qais_eu_nexus_result'));
      if (nexusRes) return 'intro';
    } catch {}
    return 'nexus_check'; // Default: start at nexus gate
  });
  const [systemDescription, setSystemDescription] = useState(() => _profile.systemPurpose || '');
  const [industry, setIndustry] = useState(() => _profile.industry || '');
  const [companyName, setCompanyName] = useState(() => _profile.companyName || '');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(() => (_profile.systemPurpose || '').length);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  // Nexus gate local form state — must be top-level (React hooks rules)
  const [localAnswers, setLocalAnswers] = useState({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null });

  // ── Rehydrate prior result on mount ──────────────────────────────────────
  React.useEffect(() => {
    try {
      if (screen === 'results') {
        const savedResult = localStorage.getItem(userKey('qais_eu_classifier_result'));
        if (savedResult) {
          const parsed = JSON.parse(savedResult);
          if (parsed.classification && parsed.reasoning) setResult(parsed);
        }
      }
    } catch {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Profile-driven locks — true if profile has a value, so the field becomes read-only
  const companyLocked = !!(_profile.companyName && _profile.companyName.trim());
  const industryLocked = !!(_profile.industry && _profile.industry.trim());

  // Description differs from profile? (only relevant if profile has one and user changed it)
  const profilePurpose = (_profile.systemPurpose || '').trim();
  const descriptionDiffers = profilePurpose && systemDescription.trim() && systemDescription.trim() !== profilePurpose;

  // Did user complete Risk Score? (drives "richer classification" banner)
  const riskResult = (() => {
    try {
      const raw = localStorage.getItem(userKey('qais_risk_result'));
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  })();
  const hasRiskScore = !!riskResult;

  const industries = [
    'Healthcare & Medical',
    'Finance & Banking',
    'HR & Recruitment',
    'Education & Training',
    'Legal & Justice',
    'Government & Public Sector',
    'Critical Infrastructure',
    'Retail & E-commerce',
    'Technology & Software',
    'Marketing & Advertising',
    'Manufacturing',
    'Other'
  ];

  // ── Determine if user's profile suggests EU AI Act applicability ──
  // eslint-disable-next-line no-unused-vars
  const isEUFocused = (() => {
    const goal = (_profile.primaryGoal || '').toLowerCase();
    const regions = (_profile.deploymentRegions || '').toLowerCase();
    return goal.includes('eu ai act') ||
           regions.includes('eu') ||
           regions.includes('europe') ||
           regions.includes('global');
  })();

  // Profile labels for display
  // eslint-disable-next-line no-unused-vars
  const profileGoalLabel = _profile.primaryGoal || 'not set';
  // eslint-disable-next-line no-unused-vars
  const profileRegionLabel = _profile.deploymentRegions || 'not set';

  // eslint-disable-next-line no-unused-vars
  const handleSkip = () => {
    try {
      localStorage.setItem(userKey('qais_eu_classifier_skipped'), JSON.stringify({
        skippedAt: Date.now(),
        reason: 'profile_indicates_non_eu',
        profile: { goal: profileGoalLabel, regions: profileRegionLabel }
      }));
      localStorage.removeItem(userKey('qais_eu_classifier_result'));
    } catch {}
    setScreen('skipped');
  };

  // ── Handle Nexus Gate Submission ──────────────────────────────────────────
  // QAIS Security: answers are boolean flags only — no PII stored
  const handleNexusSubmit = (answers) => {
    // In scope if ANY of Q1-Q5 is yes
    const inScope = answers.q1 === 'yes' || answers.q2 === 'yes' ||
                    answers.q3 === 'yes' || answers.q4 === 'yes' ||
                    answers.q5 === 'yes';
    let result;
    if (inScope) {
      result = 'in_scope';
    } else if (answers.q6 === 'yes') {
      result = 'future_expansion';
    } else {
      result = 'not_applicable';
    }
    try {
      // Store only enum values — no PII, minimum data per QAIS standard
      localStorage.setItem(userKey('qais_eu_nexus_answers'), JSON.stringify(answers));
      localStorage.setItem(userKey('qais_eu_nexus_result'), result);
      if (result !== 'in_scope') {
        localStorage.setItem(userKey('qais_eu_advisory_result'), result);
        localStorage.removeItem(userKey('qais_eu_classifier_result'));
      } else {
        localStorage.removeItem(userKey('qais_eu_advisory_result'));
      }
    } catch {}
    setNexusAnswers(answers);
    setNexusResult(result);
    if (result === 'in_scope') {
      setScreen('intro');
    } else {
      // Mark journey step as complete for non-EU companies
      // QAIS standard: no PII in history — only classification outcome stored
      try {
        incrementDocCount();
        saveDocToHistory({
          tool:      'eu_classifier',
          title:     `EU AI Act Jurisdiction — ${result === 'future_expansion' ? 'Future Readiness Advisory' : 'Not Currently Applicable'}`,
          preview:   result === 'future_expansion'
            ? 'EU AI Act not currently applicable — EU expansion planned. Advisory report generated.'
            : 'EU AI Act not currently applicable based on data jurisdiction assessment.',
          framework: 'EU AI Act',
        });
        window.dispatchEvent(new CustomEvent('qais-journey-update'));
      } catch {}
      setScreen('advisory_report');
    }
  };

  // ── Reset nexus gate ──
  const handleResetNexus = () => {
    try {
      localStorage.removeItem(userKey('qais_eu_nexus_answers'));
      localStorage.removeItem(userKey('qais_eu_nexus_result'));
      localStorage.removeItem(userKey('qais_eu_advisory_result'));
      localStorage.removeItem(userKey('qais_eu_classifier_result'));
      localStorage.removeItem(userKey('qais_eu_classifier_skipped'));
    } catch {}
    setNexusAnswers({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null });
    setLocalAnswers({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null });
    setNexusResult(null);
    setResult(null);
    setScreen('nexus_check');
  };

  // ── Build profile + risk context for enriched AI prompt ──
  const buildProfileContext = () => {
    const lines = [];
    if (_profile.companyName) lines.push(`Company: ${_profile.companyName}`);
    if (_profile.industry) lines.push(`Industry: ${_profile.industry}`);
    if (_profile.deploymentRegions) lines.push(`Deployment regions: ${_profile.deploymentRegions}`);
    if (_profile.primaryGoal) lines.push(`Primary compliance goal: ${_profile.primaryGoal}`);

    const ai = _profile.aiSystemProfile || {};
    if (ai.name) lines.push(`AI system name: ${ai.name}`);
    if (ai.modelType) lines.push(`Model/technology type: ${ai.modelType}`);
    if (ai.usesFoundationModel === true && ai.foundationModel) {
      lines.push(`Foundation model: ${ai.foundationModel} (provider/deployer split applies — EU AI Act Art. 25)`);
    } else if (ai.usesFoundationModel === false) {
      lines.push(`Foundation model: None — fully custom-built AI`);
    }
    if (ai.impactSeverity) lines.push(`Decision impact severity: ${ai.impactSeverity}`);
    if (Array.isArray(ai.dataCategories) && ai.dataCategories.length > 0) {
      lines.push(`Data categories processed: ${ai.dataCategories.join(', ')}`);
    }
    if (Array.isArray(ai.affectedUsers) && ai.affectedUsers.length > 0) {
      lines.push(`Affected user groups: ${ai.affectedUsers.join(', ')}`);
    } else if (typeof ai.affectedUsers === 'string' && ai.affectedUsers) {
      lines.push(`Affected user groups: ${ai.affectedUsers}`);
    }
    if (ai.lifecycleStage) lines.push(`Lifecycle stage: ${ai.lifecycleStage}`);
    if (Array.isArray(ai.humanOversight) && ai.humanOversight.length > 0) {
      const oversightLabels = {
        review_before: 'Human reviews AI output before action',
        periodic_audit: 'Periodic human audit of AI decisions',
        override: 'Clear way for humans to override AI',
        realtime_loop: 'Real-time human-in-the-loop monitoring',
        fully_autonomous: 'Fully autonomous (no human oversight)'
      };
      const labels = ai.humanOversight.map(v => oversightLabels[v] || v).join('; ');
      lines.push(`Human oversight measures (EU Art. 14): ${labels}`);
    }
    if (_profile.euAiRisk) lines.push(`User's self-assessed EU AI risk: ${_profile.euAiRisk}`);

    return lines.length > 0 ? lines.join('\n') : '(no structured profile available)';
  };

  const buildRiskContext = () => {
    if (!riskResult) return '(Risk Score not yet completed — recommend completing for richer signal)';
    const lines = [];
    lines.push(`Risk Score: ${riskResult.totalRiskScore}/100${riskResult.euBonus > 0 ? ` (incl. +${riskResult.euBonus} EU geographic bonus)` : ''}`);
    if (riskResult.riskLevel) lines.push(`Generic risk band: ${riskResult.riskLevel}`);
    if (riskResult.nistConcernLevel) lines.push(`NIST AI RMF concern level: ${riskResult.nistConcernLevel}`);
    if (riskResult.nistMaturity) lines.push(`NIST AI RMF maturity: ${riskResult.nistMaturity}`);
    if (typeof riskResult.isoReadiness === 'number') lines.push(`ISO 42001 readiness: ${riskResult.isoReadiness}%`);
    if (riskResult.primaryFramework) lines.push(`User's primary framework focus: ${riskResult.primaryFramework}`);
    if (typeof riskResult.euApplicable === 'boolean') {
      lines.push(`EU AI Act applicability per profile: ${riskResult.euApplicable ? 'applicable' : 'not directly applicable'}`);
    }
    return lines.join('\n');
  };

  // ── Classify AI System ──
  const classifySystem = async () => {
    if (!systemDescription.trim() || !industry) return;
    setLoading(true);
    setScreen('loading');

    const profileContext = buildProfileContext();
    const riskContext = buildRiskContext();

    const prompt = `You are an EU AI Act compliance expert. Classify the following AI system under the EU AI Act (Regulation EU 2024/1689).

USER PROFILE (from prior onboarding — use this signal):
${profileContext}

RISK ASSESSMENT (from prior multi-framework risk calculator — use this signal):
${riskContext}

USER-PROVIDED SYSTEM DESCRIPTION (focus your classification on this):
${systemDescription}

Use the profile and risk data above to inform your classification — for example:
- If "Affected user groups" includes vulnerable populations, weigh Article 5 prohibited practices and Annex III categories
- If "Foundation model" is set, consider provider/deployer split (Article 25)
- If "Data categories" includes biometric/health/financial, weigh corresponding Annex III categories
- If "Human oversight" is "fully autonomous", weigh Article 14 obligations heavily
- If "Impact severity" is high/critical, lean toward higher classifications when ambiguous

Provide a JSON response with EXACTLY this structure (no markdown, no backticks, pure JSON only):
{
  "classification": "unacceptable|high|limited|minimal",
  "confidence": "high|medium|low",
  "reasoning": "2-3 sentence plain English explanation of WHY this classification was assigned, referencing specific EU AI Act articles AND the relevant profile/risk signals you used",
  "primary_article": "The most relevant EU AI Act article (e.g. Article 5, Annex III Point 4)",
  "key_concern": "The single most important compliance concern for this system in one sentence",
  "similar_examples": ["example 1 of similar AI system", "example 2"],
  "top_obligations": [
    {"obligation": "Most urgent action required", "article": "Article X", "deadline": "When"},
    {"obligation": "Second most urgent action", "article": "Article X", "deadline": "When"},
    {"obligation": "Third action", "article": "Article X", "deadline": "When"}
  ],
  "prohibited_flags": ["List any Article 5 prohibited practice concerns, empty array if none"],
  "high_risk_category": "If high risk, which Annex III category applies, empty string if not high risk",
  "next_step": "The single most important thing this organization should do in the next 30 days"
}

Classification rules:
- unacceptable: if the system matches ANY Article 5 prohibited practice
- high: if it matches ANY Annex III category (biometric ID, critical infrastructure, education assessment, employment/HR, essential services like credit/insurance, law enforcement, migration, justice)
- limited: if it interacts with humans as AI (chatbots), generates content, or uses emotion recognition — without being high risk
- minimal: everything else (spam filters, recommendations, games, inventory, search)

Be precise. If there is ANY doubt between high and limited, choose high. If there is ANY doubt between unacceptable and high, choose unacceptable.`;

    try {
      const content = await callLLM({
        messages: [
          {
            role: 'system',
            content: 'You are an EU AI Act expert. You use both the user-provided description AND the structured profile/risk data to reach a precise classification. Always respond with pure valid JSON only — no markdown, no backticks, no explanation outside JSON.'
          },
          { role: 'user', content: prompt }
        ],
        temperature: 0.2,
        max_tokens: 1000
      });

      // Parse JSON response
      let parsed;
      try {
        const cleaned = content.replace(/```json|```/g, '').trim();
        parsed = JSON.parse(cleaned);
      } catch {
        // Fallback if JSON parsing fails
        parsed = {
          classification: 'high',
          confidence: 'low',
          reasoning: 'Unable to parse AI response. Based on your description, a High Risk classification is applied as a precautionary measure. Please review manually.',
          primary_article: 'Annex III',
          key_concern: 'Manual review required',
          similar_examples: [],
          top_obligations: [
            { obligation: 'Conduct manual EU AI Act assessment', article: 'Article 9', deadline: 'Immediately' }
          ],
          prohibited_flags: [],
          high_risk_category: '',
          next_step: 'Consult a qualified EU AI Act legal professional'
        };
      }

      setResult(parsed);
      setScreen('results');
      incrementDocCount();
      saveDocToHistory({
        tool: 'eu_classifier',
        title: `EU AI Act Classification${companyName ? ' — ' + companyName : ''}`,
        preview: `Classification: ${parsed.classification} risk. ${parsed.reasoning?.slice(0, 80) || ''}`,
        framework: 'EU AI Act',
      });

    // Save FULL result to localStorage so it rehydrates on mount with all details
      try {
        localStorage.setItem(userKey('qais_eu_classifier_result'), JSON.stringify(parsed));
        // Clear any prior skip flag — running real classification supersedes a skip
        localStorage.removeItem(userKey('qais_eu_classifier_skipped'));
        // Notify journey bar to re-check
        window.dispatchEvent(new CustomEvent('qais-journey-update'));
      } catch {}

      // Persist server-side — real source of truth for journey-completion gating
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (user) {
          await supabase.from('user_documents').insert({
            user_id:      user.id,
            doc_type:     'eu_classifier',
            content:      JSON.stringify(parsed),
            framework:    'EU AI Act',
            generated_at: new Date().toISOString(),
          });
        }
      } catch (e) {
        console.log('Supabase EU classifier save failed; localStorage retains it:', e.message);
      }



    } catch (error) {
      console.error('Classification failed:', error);
      const fallbackResult = {
        classification: 'high',
        confidence: 'low',
        reasoning: 'Classification service is temporarily unavailable. A High Risk classification has been applied as a precautionary measure.',
        primary_article: 'Manual review required',
        key_concern: 'Please retry or consult a compliance professional',
        similar_examples: [],
        top_obligations: [
          { obligation: 'Retry classification or consult compliance expert', article: 'N/A', deadline: 'As soon as possible' }
        ],
        prohibited_flags: [],
        high_risk_category: '',
        next_step: 'Please try again or contact QuantumAIShield for manual assessment'
      };
      setResult(fallbackResult);
      try {
        localStorage.setItem(userKey('qais_eu_classifier_result'), JSON.stringify(fallbackResult));
        window.dispatchEvent(new CustomEvent('qais-journey-update'));
      } catch {}
      setScreen('results');
    }
    setLoading(false);
  };

  // ── Download Report as PDF (using html2canvas + jsPDF to preserve layout) ──
// ── Helper: load image as base64 ──
  const loadImageAsBase64 = async (path) => {
    try {
      const response = await fetch(path);
      const blob = await response.blob();
      return await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
    } catch (e) {
      console.warn('Logo load failed:', e);
      return null;
    }
  };

  // ── Helper: get classification color band ──
  const getClassificationColor = (classification) => {
    const colors = {
      unacceptable: { bg: '#fee2e2', border: '#dc2626', text: '#991b1b', label: 'UNACCEPTABLE RISK — PROHIBITED' },
      high:         { bg: '#ffedd5', border: '#ea580c', text: '#9a3412', label: 'HIGH RISK' },
      limited:      { bg: '#fef9c3', border: '#ca8a04', text: '#854d0e', label: 'LIMITED RISK' },
      minimal:      { bg: '#dcfce7', border: '#16a34a', text: '#166534', label: 'MINIMAL RISK' },
    };
    return colors[classification] || colors.minimal;
  };

  // ── Download Report as PDF (styled, with logo cover + per-page header/footer) ──
  const downloadPDFReport = async () => {
    if (!result) return;
    setPdfGenerating(true);
    await new Promise(resolve => setTimeout(resolve, 50));

    const logoBase64 = await loadImageAsBase64('/logo.png');
    const level = classifierData.risk_levels[result.classification];
    const obligations = level?.obligations || [];
    const colorBand = getClassificationColor(result.classification);

    const topObligationsHTML = (result.top_obligations || []).map((o, i) => `
      <div style="background: #f8fafc; border-left: 4px solid #0e7c7c; padding: 12px 16px; margin-bottom: 10px; border-radius: 6px; page-break-inside: avoid;">
        <div style="font-weight: 700; color: #0e7c7c; font-size: 15px; margin-bottom: 6px;">${i + 1}. ${o.obligation}</div>
        <div style="font-size: 14px; color: #64748b;"><strong>Reference:</strong> ${o.article} &nbsp;|&nbsp; <strong>Deadline:</strong> ${o.deadline}</div>
      </div>
    `).join('');

    const fullObligationsHTML = obligations.map((o) => `
      <li style="margin-bottom: 6px;">
        <strong>${o.title}</strong>
        <span style="color: #64748b; font-size: 14px;"> [${o.ref}]</span>
        ${o.urgent ? '<span style="background: #fee2e2; color: #991b1b; padding: 2px 8px; border-radius: 4px; font-size: 12px; font-weight: 700; margin-left: 6px;">URGENT</span>' : ''}
      </li>
    `).join('');

    const finesHTML = (classifierData.fines || []).map(f => `
      <div style="background: #f8fafc; border-left: 4px solid #ef4444; padding: 10px 14px; margin-bottom: 8px; border-radius: 6px; page-break-inside: avoid;">
        <div style="font-weight: 700; color: #991b1b; font-size: 14px; margin-bottom: 4px;">Tier ${f.tier}: ${f.violation}</div>
        <div style="font-size: 14px; color: #1e293b;"><strong>Fine:</strong> ${f.fine}</div>
      </div>
    `).join('');

    const htmlContent = `
      <div style="font-family: 'Inter', 'Helvetica', sans-serif; max-width: 800px; margin: 0 auto; padding: 20px; background: white; color: #1e293b; line-height: 1.6; font-size: 16px;">
        ${logoBase64 ? `<div style="text-align: center; margin-bottom: 8px;"><img src="${logoBase64}" style="max-width: 180px; max-height: 90px;" alt="QuantumAI Shield" /></div>` : ''}
        <h1 style="color: #0e7c7c; border-bottom: 3px solid #0e7c7c; padding-bottom: 10px; font-size: 28px; text-align: center;">EU AI Act Classification Report</h1>
        <p style="text-align: center;"><strong>Generated by QuantumAI Shield</strong><br>${new Date().toLocaleDateString()}</p>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Company & Industry</h2>
        <p><strong>Company:</strong> ${companyName || 'Not specified'}<br>
        <strong>Industry:</strong> ${industry}</p>

        <div style="background: ${colorBand.bg}; border: 3px solid ${colorBand.border}; border-radius: 12px; padding: 20px; margin: 20px 0; text-align: center;">
          <div style="font-size: 14px; color: ${colorBand.text}; font-weight: 600; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 8px;">Classification Result</div>
          <div style="font-size: 26px; color: ${colorBand.text}; font-weight: 800; margin-bottom: 8px;">${colorBand.label}</div>
          <div style="font-size: 14px; color: ${colorBand.text};">
            <strong>Reference:</strong> ${result.primary_article || 'EU AI Act'}
            &nbsp;|&nbsp; <strong>Confidence:</strong> ${(result.confidence || 'medium').toUpperCase()}
          </div>
        </div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">AI System Description</h2>
        <div style="background: #f8fafc; padding: 14px 18px; border-radius: 8px; border-left: 4px solid #0e7c7c; font-size: 15px;">${(systemDescription || '').replace(/\n/g, '<br>')}</div>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Classification Details</h2>
        <p><strong>Enforcement:</strong> ${level?.enforcement || '—'}<br>
        <strong>Potential Penalty:</strong> ${level?.penalty || '—'}</p>

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Why This Classification</h2>
        <div style="background: #f1f5f9; padding: 14px 18px; border-radius: 8px; font-size: 15px;">${(result.reasoning || '').replace(/\n/g, '<br>')}</div>

        ${result.key_concern ? `
          <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Key Concern</h2>
          <div style="background: #fef3c7; border-left: 4px solid #f59e0b; padding: 14px 18px; border-radius: 8px; font-size: 15px;">${result.key_concern}</div>
        ` : ''}

        ${result.prohibited_flags?.length > 0 ? `
          <h2 style="color: #dc2626; border-bottom: 2px solid #dc2626; font-size: 22px;">⚠️ Prohibited Practice Flags</h2>
          <div style="background: #fee2e2; border: 2px solid #dc2626; border-radius: 8px; padding: 14px 18px; font-size: 15px;">
            <ul style="margin: 0; padding-left: 20px;">${result.prohibited_flags.map(f => `<li style="margin-bottom: 4px;">${f}</li>`).join('')}</ul>
          </div>
        ` : ''}

        ${result.high_risk_category ? `
          <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">High Risk Category</h2>
          <p style="background: #ffedd5; border-left: 4px solid #ea580c; padding: 12px 16px; border-radius: 8px; font-size: 15px; margin: 0;"><strong>${result.high_risk_category}</strong></p>
        ` : ''}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Top 3 Immediate Obligations</h2>
        ${topObligationsHTML || '<p style="color: #64748b;">No immediate obligations specified.</p>'}

        ${obligations.length > 0 ? `
          <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Full Compliance Obligations (${level?.label})</h2>
          <ul style="font-size: 15px; padding-left: 22px;">${fullObligationsHTML}</ul>
        ` : ''}

        ${result.next_step ? `
          <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Your Next 30 Days</h2>
          <div style="background: #dbeafe; border-left: 4px solid #2563eb; padding: 14px 18px; border-radius: 8px; font-size: 15px;">${result.next_step}</div>
        ` : ''}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">EU AI Act Full Deadline Timeline</h2>
        ${EU_DEADLINES.map(d => { const status = getEuTimelineStatus(d.iso); return `
          <div style="display: flex; gap: 16px; align-items: flex-start; padding: 10px 14px; margin-bottom: 8px; border-radius: 8px; background: ${status === 'PAST' ? '#f8fafc' : status === 'ACTIVE' ? '#fef3c7' : '#eff6ff'}; border: 2px solid ${status === 'PAST' ? '#e2e8f0' : status === 'ACTIVE' ? '#fbbf24' : '#93c5fd'}; page-break-inside: avoid;">
            <div style="min-width: 110px; flex-shrink: 0;">
              <span style="font-size: 12px; font-weight: 700; color: ${status === 'PAST' ? '#94a3b8' : status === 'ACTIVE' ? '#92400e' : '#1e40af'}">${d.date}</span><br/>
              <span style="font-size: 10px; font-weight: 700; padding: 1px 6px; border-radius: 4px; background: ${status === 'PAST' ? '#e2e8f0' : status === 'ACTIVE' ? '#fbbf24' : '#bfdbfe'}; color: ${status === 'PAST' ? '#64748b' : status === 'ACTIVE' ? '#92400e' : '#1e40af'}">${status}</span>
            </div>
            <div>
              <strong style="font-size: 13px; color: #0f172a;">${d.label}</strong><br/>
              <span style="font-size: 12px; color: #64748b;">${d.detail}</span>
            </div>
          </div>
        `; }).join('')}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">EU AI Act Fines (Reference)</h2>
        ${finesHTML}

        <h2 style="color: #0e7c7c; border-bottom: 2px solid #0e7c7c; font-size: 22px;">Disclaimer</h2>
        <p style="font-size: 12px; color: #64748b;">${classifierData.meta?.disclaimer || 'AI-generated classification is for informational purposes only and does not constitute legal advice.'}</p>
      </div>
    `;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.style.width = '800px';
    container.style.backgroundColor = 'white';
    container.style.zIndex = '-1';
    container.innerHTML = htmlContent;
    document.body.appendChild(container);

    try {
      await new Promise(resolve => setTimeout(resolve, 300));
      const canvas = await html2canvas(container, { scale: 2, backgroundColor: '#ffffff', logging: false, useCORS: false });

      const pdf = new jsPDF('p', 'mm', 'a4');
      const pdfWidth  = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

const HEADER_HEIGHT = 28;
      const FOOTER_HEIGHT = 22;
      const SIDE_MARGIN   = 14; // mm — page-level left/right margin (was 0, edge-to-edge)

      const usableWidth  = pdfWidth - 2 * SIDE_MARGIN;
      const usableHeight = pdfHeight - HEADER_HEIGHT - FOOTER_HEIGHT;
      const imgWidth     = usableWidth;
      const imgHeight    = (canvas.height * imgWidth) / canvas.width;

      // ── Smart page breaks: find blank rows in canvas to slice at clean boundaries ──
      const canvasCtx = canvas.getContext('2d');
      const imageData = canvasCtx.getImageData(0, 0, canvas.width, canvas.height);
      const data = imageData.data;
      const cw = canvas.width;

      const isRowBlank = (y) => {
        // A row is "safe to slice at" if it has very few dark pixels
        let darkPixels = 0;
        for (let x = 0; x < cw; x += 4) {
          const i = (y * cw + x) * 4;
          if (data[i] < 100 || data[i + 1] < 100 || data[i + 2] < 100) {
            darkPixels++;
            if (darkPixels > 2) return false; // 3+ dark pixels = has text
          }
        }
        return true;
      };

      // Canvas pixels per mm — used to convert page heights to canvas-space
      const pxPerMm = canvas.height / imgHeight;
      const usableHeightPx = Math.floor(usableHeight * pxPerMm);

      // Find blank rows once (sample every 2 pixels for speed)
      const blankRows = [];
      for (let y = 0; y < canvas.height; y += 2) {
        if (isRowBlank(y)) blankRows.push(y);
      }

      // Compute page break points: for each page, find the LAST blank row before the boundary
const pageBreaks = [0];
      let currentY = 0;
      while (currentY < canvas.height) {
        const target = currentY + usableHeightPx;
        if (target >= canvas.height) break;
        let breakAt = target;
        const minAllowed = currentY + usableHeightPx * 0.35;
        for (let i = blankRows.length - 1; i >= 0; i--) {
          if (blankRows[i] <= target && blankRows[i] > minAllowed) {
            breakAt = blankRows[i];
            break;
          }
        }
        pageBreaks.push(breakAt);
        currentY = breakAt;
      }

      // ── Drop the last page if it has no meaningful content (avoid blank trailing page) ──
      // Find the last non-blank row in the canvas
      let lastContentRow = 0;
      for (let y = canvas.height - 1; y >= 0; y -= 2) {
        if (!isRowBlank(y)) { lastContentRow = y; break; }
      }
      // If the last page break is AFTER the last content row, that page would be blank — remove it
      while (pageBreaks.length > 1 && pageBreaks[pageBreaks.length - 1] >= lastContentRow - 20) {
        pageBreaks.pop();
      }
      const totalPages = pageBreaks.length;

      const drawHeaderFooter = (pageNum) => {
        if (pageNum > 1) {
          const titleText = 'QuantumAI Shield · EU AI Act Classification';
          pdf.setFontSize(11);
          pdf.setTextColor(80, 80, 80);
          const logoW  = logoBase64 ? 10 : 0;
          const gap    = logoBase64 ? 3  : 0;
          const titleW = pdf.getTextWidth(titleText);
          const groupW = logoW + gap + titleW;
          const groupStartX = (pdfWidth - groupW) / 2;
          if (logoBase64) { try { pdf.addImage(logoBase64, 'PNG', groupStartX, 6, 10, 10); } catch {} }
          pdf.text(titleText, groupStartX + logoW + gap, 13);
          pdf.setDrawColor(200, 200, 200);
          pdf.setLineWidth(0.2);
          pdf.line(SIDE_MARGIN + 4, HEADER_HEIGHT - 4, pdfWidth - SIDE_MARGIN - 4, HEADER_HEIGHT - 4);
        }
        pdf.setFontSize(9);
        pdf.setTextColor(120, 120, 120);
        pdf.line(SIDE_MARGIN + 4, pdfHeight - FOOTER_HEIGHT + 6, pdfWidth - SIDE_MARGIN - 4, pdfHeight - FOOTER_HEIGHT + 6);
        pdf.text('www.QuantumAIShield.com',          SIDE_MARGIN + 4,            pdfHeight - 8);
        pdf.text(`Page ${pageNum} of ${totalPages}`, pdfWidth / 2,               pdfHeight - 8, { align: 'center' });
        pdf.text(new Date().toLocaleDateString(),    pdfWidth - SIDE_MARGIN - 4, pdfHeight - 8, { align: 'right' });
      };


// ── For each page, cut the canvas to that page's slice and add it ──
      for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
        if (pageNum > 1) pdf.addPage();

        const startPx = pageBreaks[pageNum - 1];
        const endPx   = pageNum < totalPages ? pageBreaks[pageNum] : canvas.height;
        const slicePx = endPx - startPx;
        const sliceMm = slicePx / pxPerMm;

        // Create a temp canvas containing ONLY this page's slice
        const tempCanvas = document.createElement('canvas');
        tempCanvas.width  = canvas.width;
        tempCanvas.height = slicePx;
        const tempCtx = tempCanvas.getContext('2d');
        tempCtx.fillStyle = '#ffffff';
        tempCtx.fillRect(0, 0, canvas.width, slicePx);
        tempCtx.drawImage(canvas, 0, startPx, canvas.width, slicePx, 0, 0, canvas.width, slicePx);
        const sliceData = tempCanvas.toDataURL('image/png');

        // Place the slice in the usable area between header and footer
        pdf.addImage(sliceData, 'PNG', SIDE_MARGIN, HEADER_HEIGHT, imgWidth, sliceMm);

        // Header + footer overlay
        pdf.setFillColor(255, 255, 255);
        pdf.rect(0, 0, pdfWidth, HEADER_HEIGHT, 'F');
        pdf.rect(0, pdfHeight - FOOTER_HEIGHT, pdfWidth, FOOTER_HEIGHT, 'F');
        drawHeaderFooter(pageNum);
      }



      pdf.save(`EU_AI_Act_Classification_${(companyName || 'Report').replace(/\s+/g, '_')}_${Date.now()}.pdf`);
    } catch (err) {
      console.error('PDF generation error:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      document.body.removeChild(container);
      setPdfGenerating(false);
    }
  };

  // ── Reset classifier (goes back to nexus gate — every run starts there) ──
  const handleReset = () => {
    setScreen('nexus_check');
    setSystemDescription(_profile.systemPurpose || '');
    setIndustry(_profile.industry || '');
    setCompanyName(_profile.companyName || '');
    setResult(null);
    setCharCount((_profile.systemPurpose || '').length);
    setLocalAnswers({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null });
    try {
      localStorage.removeItem(userKey('qais_eu_classifier_result'));
      localStorage.removeItem(userKey('qais_eu_nexus_answers'));
      localStorage.removeItem(userKey('qais_eu_nexus_result'));
      localStorage.removeItem(userKey('qais_eu_advisory_result'));
    } catch {}
  };

  const level = result ? classifierData.risk_levels[result.classification] : null;

  // ════════════════════════════════
  // NEXUS CHECK SCREEN
  // ════════════════════════════════
  if (screen === 'nexus_check') {
    const allAnswered = localAnswers.q1 && localAnswers.q2 && localAnswers.q3 &&
                        localAnswers.q4 && localAnswers.q5 && localAnswers.q6;
    const answered = Object.values(localAnswers).filter(v => v !== null).length;

    return (
      <div style={{ padding: '40px', maxWidth: '760px', margin: '0 auto' }}>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
         <div style={{ marginBottom: '12px' }}><EUFlagIcon size={70} /></div>
          <h1 style={{ fontSize: '30px', fontWeight: 'bold', color: '#0f172a', marginBottom: '10px' }}>
            EU AI Act — Jurisdiction Check
          </h1>
          <p style={{ fontSize: '15px', color: '#64748b', lineHeight: '1.6', maxWidth: '560px', margin: '0 auto' }}>
            Answer all 6 questions honestly. Your answers determine whether the EU AI Act applies to
            your organisation. Full classification only runs if you are in scope.
          </p>
        </div>

        {/* Legal warning */}
        <div style={{ backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px', padding: '14px 18px', marginBottom: '24px' }}>
          <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.6' }}>
            <strong>⚠️ Important:</strong> The EU AI Act has extraterritorial reach. A single EU customer,
            a single EU user affected by your AI output, or a single EU partner embedding your system
            is sufficient to put you in scope — regardless of where your company is headquartered.
          </p>
        </div>

        {/* 6 Questions — all mandatory, Yes/No only, no bypass */}
        <YesNo id="q1" article="Art. 2(1)(a)"
          label="Do you build or sell AI systems placed on the EU market or put into service in the EU?"
          sublabel="Includes any SaaS, API, or AI product accessible to EU customers — even if sold from outside the EU."
          answers={localAnswers} setAnswers={setLocalAnswers}
        />
        <YesNo id="q2" article="Art. 2(1)(b)"
          label="Is your organisation located in the EU and do you use AI systems?"
          sublabel="Any EU-based company deploying AI — whether built in-house or procured from a vendor."
          answers={localAnswers} setAnswers={setLocalAnswers}
        />
        <YesNo id="q3" article="Art. 2(1)(c)"
          label="Do your AI system's outputs affect individuals located in the EU, even if your company is outside the EU?"
          sublabel="This is the most commonly missed trigger. If your AI makes decisions, scores, or recommendations that impact EU residents — you are in scope."
          answers={localAnswers} setAnswers={setLocalAnswers}
        />
        <YesNo id="q4" article="GDPR Overlap"
          label="Does your AI system process personal data of EU residents?"
          sublabel="Includes data collected directly, via partners, resellers, or third-party integrations."
          answers={localAnswers} setAnswers={setLocalAnswers}
        />
        <YesNo id="q5" article="Art. 25"
          label="Do any EU-based partners or customers embed or resell your AI system in their own products?"
          sublabel="If an EU company builds on top of your AI via API or integration, you are a provider under the Act."
          answers={localAnswers} setAnswers={setLocalAnswers}
        />
        <YesNo id="q6" article="Future Scope"
          label="Do you plan to expand to EU markets or onboard EU customers within the next 24 months?"
          sublabel="Even if none of the above apply today, planned EU expansion means you should prepare now."
          answers={localAnswers} setAnswers={setLocalAnswers}
        />

        {/* Progress bar */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '13px', color: '#64748b' }}>{answered} of 6 answered</span>
            <span style={{ fontSize: '13px', fontWeight: allAnswered ? '700' : '400',
              color: allAnswered ? '#10b981' : '#64748b' }}>
              {allAnswered ? '✓ Ready to check' : 'Answer all 6 to continue'}
            </span>
          </div>
          <div style={{ height: '6px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
            <div style={{ height: '100%', borderRadius: '4px', transition: 'width 0.3s',
              backgroundColor: allAnswered ? '#10b981' : '#3b82f6',
              width: `${(answered / 6) * 100}%` }} />
          </div>
        </div>

        {/* Submit — hard gate, only active when all 6 answered */}
        <button
          onClick={() => allAnswered && handleNexusSubmit(localAnswers)}
          disabled={!allAnswered}
          style={{
            width: '100%', padding: '18px',
            backgroundColor: allAnswered ? '#3b82f6' : '#cbd5e1',
            color: 'white', border: 'none', borderRadius: '12px',
            fontSize: '18px', fontWeight: 'bold',
            cursor: allAnswered ? 'pointer' : 'not-allowed',
            marginBottom: '14px', transition: 'background-color 0.2s'
          }}
        >
          {allAnswered ? '🇪🇺 Check My EU AI Act Status →' : 'Answer all 6 questions to continue'}
        </button>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', margin: 0 }}>
          Your answers are saved locally and never sent to our servers. You can re-check jurisdiction at any time.
        </p>
      </div>
    );
  }

  // ════════════════════════════════
  // ADVISORY REPORT SCREEN
  // ════════════════════════════════
  if (screen === 'advisory_report') {
    const isExpanding = nexusResult === 'future_expansion';

    const triggerList = [
      'You onboard a single EU customer or user',
      'Your AI outputs affect EU individuals (even indirectly through a partner)',
      'An EU-based business embeds or resells your AI system',
      'You deploy any AI system accessible from the EU (e.g. a public-facing SaaS or API)',
    ];

    const whatChanges = [
      { icon: '📢', title: 'Article 50 — Transparency (Aug 2, 2026)', detail: 'Chatbot disclosure ("You are interacting with AI"), AI content labelling — applies immediately when in scope.' },
      { icon: '📋', title: 'Article 11 — Technical Documentation', detail: 'Detailed documentation of AI system purpose, design, data used, and limitations.' },
      { icon: '⚠️', title: 'Article 9 — Risk Management System', detail: 'Continuous risk management process for high-risk AI systems — not a one-time assessment.' },
      { icon: '👁️', title: 'Article 14 — Human Oversight', detail: 'High-risk AI systems must support meaningful human intervention and override capability.' },
      { icon: '🌍', title: 'Article 22 — EU Authorised Representative', detail: 'Non-EU providers of high-risk systems must designate an EU representative.' },
      { icon: '🗄️', title: 'Article 49 — EU Database Registration', detail: 'High-risk AI systems must be registered in the EU AI database before deployment.' },
      { icon: '🔐', title: 'Article 10 — Data Governance', detail: 'Training data must be relevant, representative, and documented for high-risk systems.' },
    ];

    return (
      <div style={{ padding: '40px', maxWidth: '860px', margin: '0 auto' }}>

        {/* Status banner */}
        <div style={{
          backgroundColor: isExpanding ? '#fffbeb' : '#f0fdf4',
          border: `3px solid ${isExpanding ? '#f59e0b' : '#86efac'}`,
          borderRadius: '20px', padding: '32px', textAlign: 'center', marginBottom: '28px'
        }}>
          <div style={{ fontSize: '56px', marginBottom: '12px' }}>{isExpanding ? '🔭' : '✅'}</div>
          <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: isExpanding ? '#92400e' : '#166534', margin: '0 0 10px' }}>
            {isExpanding ? 'EU AI Act: Not Currently Applicable — Prepare Now' : 'EU AI Act: Not Currently Applicable'}
          </h1>
          <p style={{ fontSize: '15px', color: isExpanding ? '#92400e' : '#166534', margin: '0 auto', maxWidth: '560px', lineHeight: '1.6' }}>
            {isExpanding
              ? `Based on your answers, the EU AI Act does not apply to your organisation today. However, you plan to expand to EU markets ${nexusResult === 'future_12' ? 'within 12 months' : 'within 24 months'} — preparation now is significantly faster and cheaper than reactive compliance later.`
              : 'Based on your answers, the EU AI Act does not currently apply to your organisation. You have no EU customers, no EU data processing, and no planned EU expansion at this time.'}
          </p>
        </div>

        {/* What would trigger scope */}
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 16px' }}>
            ⚡ What Would Put You In Scope (Immediately)
          </h2>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 14px' }}>
            Any of these changes to your business would trigger EU AI Act obligations:
          </p>
          {triggerList.map((t, i) => (
            <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px 0', borderBottom: i < triggerList.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
              <span style={{ color: '#ef4444', fontWeight: '700', fontSize: '16px', flexShrink: 0 }}>⚠</span>
              <p style={{ fontSize: '14px', color: '#334155', margin: 0, lineHeight: '1.6' }}>{t}</p>
            </div>
          ))}
          <div style={{ backgroundColor: '#fef3c7', border: '1px solid #fbbf24', borderRadius: '8px', padding: '12px', marginTop: '14px' }}>
            <p style={{ fontSize: '13px', color: '#92400e', margin: 0 }}>
              <strong>⚠️ If any of the above applies to you now:</strong> re-run this check and answer the questions again — you may already be in scope.
            </p>
          </div>
        </div>

        {/* What changes when you expand */}
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 6px' }}>
            📋 What Changes When You Expand to EU
          </h2>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 16px' }}>
            These obligations apply immediately upon becoming in scope — not at some future deadline.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {whatChanges.map((item, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '2px solid #e2e8f0' }}>
                <span style={{ fontSize: '22px', flexShrink: 0 }}>{item.icon}</span>
                <div>
                  <p style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a', margin: '0 0 4px' }}>{item.title}</p>
                  <p style={{ fontSize: '13px', color: '#64748b', margin: 0, lineHeight: '1.5' }}>{item.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* EU AI Act full deadline timeline */}
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 16px' }}>
            📅 EU AI Act Full Deadline Timeline
          </h2>
          {EU_DEADLINES.map((d, i) => {
            const status = getEuTimelineStatus(d.iso);
            return (
            <div key={i} style={{
              display: 'flex', gap: '16px', alignItems: 'flex-start',
              padding: '12px 14px', marginBottom: '8px', borderRadius: '8px',
              backgroundColor: status === 'PAST' ? '#f8fafc' : status === 'ACTIVE' ? '#fef3c7' : '#f0f9ff',
              border: `2px solid ${status === 'PAST' ? '#e2e8f0' : status === 'ACTIVE' ? '#fbbf24' : '#93c5fd'}`,
            }}>
              <div style={{ minWidth: '110px', flexShrink: 0 }}>
                <span style={{ fontSize: '12px', fontWeight: '700',
                  color: status === 'PAST' ? '#94a3b8' : status === 'ACTIVE' ? '#92400e' : '#1e40af'
                }}>{d.date}</span>
                <div style={{ marginTop: '2px' }}>
                  <span style={{ fontSize: '10px', fontWeight: '700', padding: '1px 6px', borderRadius: '4px',
                    backgroundColor: status === 'PAST' ? '#e2e8f0' : status === 'ACTIVE' ? '#fef3c7' : '#eff6ff',
                    color: status === 'PAST' ? '#64748b' : status === 'ACTIVE' ? '#d97706' : '#2563eb',
                    border: `1px solid ${status === 'PAST' ? '#cbd5e1' : status === 'ACTIVE' ? '#fbbf24' : '#93c5fd'}`
                  }}>{status}</span>
                </div>
              </div>
              <div>
                <p style={{ fontSize: '14px', fontWeight: '600', color: '#0f172a', margin: '0 0 2px' }}>{d.label}</p>
                <p style={{ fontSize: '12px', color: '#64748b', margin: 0, lineHeight: '1.4' }}>{d.detail}</p>
              </div>
            </div>
            );
          })}
        </div>

        {/* Recommended actions */}
        {isExpanding && (
          <div style={{ backgroundColor: '#eff6ff', border: '2px solid #93c5fd', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '18px', fontWeight: 'bold', color: '#1e40af', margin: '0 0 14px' }}>
              🚀 Recommended Actions Before EU Expansion
            </h2>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {[
                'Classify all AI systems you plan to deploy in the EU against Annex III high-risk categories',
                'Identify your role: are you a Provider (building AI), Deployer (using AI), or both?',
                'Begin technical documentation (Article 11) — easier built in than retrofitted',
                'Design human oversight mechanisms into your AI systems (Article 14)',
                'Assess whether you need an EU Authorised Representative (Article 22)',
                'Enable Article 50 transparency disclosures: "You are interacting with AI" on all chatbots',
                'Implement machine-readable AI content labelling (C2PA standard) for generated content',
                'Run the full EU AI Act classifier on this platform when you are ready to go live in the EU',
              ].map((action, i) => (
                <div key={i} style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                  <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: '#3b82f6', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '11px', flexShrink: 0, marginTop: '1px' }}>{i + 1}</div>
                  <p style={{ fontSize: '14px', color: '#1e40af', margin: 0, lineHeight: '1.6' }}>{action}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ISO 42001 recommendation */}
        <div style={{ backgroundColor: '#f0fdf4', border: '2px solid #86efac', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#166534', margin: '0 0 10px' }}>
            💡 What You Can Do Right Now
          </h3>
          <p style={{ fontSize: '14px', color: '#166534', margin: '0 0 10px', lineHeight: '1.6' }}>
            Even without EU AI Act obligations today, ISO 42001 and NIST AI RMF apply globally and build the governance foundation you'll need for EU compliance when the time comes.
            Continue your compliance journey using the other frameworks — you'll be well-prepared when you expand to the EU.
          </p>
          {onNavigate && (
            <button
              onClick={() => onNavigate('dashboard')}
              style={{ padding: '12px 24px', backgroundColor: '#16a34a', color: 'white', border: 'none', borderRadius: '8px', fontSize: '14px', fontWeight: '700', cursor: 'pointer' }}
            >
              ← Continue Compliance Journey
            </button>
          )}
        </div>

        {/* Action buttons — re-check only, no bypass */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={handleResetNexus}
            style={{ flex: 1, padding: '14px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '15px', fontWeight: '600', cursor: 'pointer', minWidth: '200px' }}
          >
            🔄 Re-check My Jurisdiction
          </button>
        </div>

        <p style={{ textAlign: 'center', fontSize: '12px', color: '#94a3b8', margin: '16px 0 0' }}>
          ⚠️ Advisory only — not legal advice. If you have any EU operations or EU customers, consult a qualified EU AI Act legal professional.
        </p>
      </div>
    );
  }

  // ════════════════════════════════
  // SKIPPED CONFIRMATION SCREEN
  // ════════════════════════════════
  if (screen === 'skipped') {
    return (
      <div style={{ padding: '40px', maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '20px' }}>⏭️</div>
        <h1 style={{ fontSize: '32px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px' }}>
          EU AI Act Classification Skipped
        </h1>
        <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '24px', lineHeight: '1.6' }}>
          We've marked EU AI Act as "Not Applicable" in your compliance journey. Your journey progress will continue forward.
        </p>
        <div style={{ backgroundColor: '#f0fdf4', border: '2px solid #bbf7d0', borderRadius: '12px', padding: '20px', marginBottom: '24px', textAlign: 'left' }}>
          <p style={{ fontSize: '14px', color: '#065f46', margin: '0 0 8px', fontWeight: '600' }}>
            ℹ️ Want to come back to this later?
          </p>
          <p style={{ fontSize: '13px', color: '#065f46', margin: 0, lineHeight: '1.6' }}>
            You can return to this classifier anytime from the top navigation. If you start serving EU customers later, your AI may become subject to EU AI Act — running this check then will replace the "skipped" status with a real classification.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {onNavigate && (
            <button
              onClick={() => onNavigate('dashboard')}
              style={{ padding: '14px 28px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer' }}
            >
              ← Back to Dashboard
            </button>
          )}
          <button
            onClick={() => { setLocalAnswers({ q1: null, q2: null, q3: null, q4: null, q5: null, q6: null }); setScreen('nexus_check'); }}
            style={{ padding: '14px 28px', backgroundColor: 'white', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
          >
            Run EU AI Act Jurisdiction Check
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════
  // LOADING SCREEN
  // ════════════════════════════════
  if (screen === 'loading') {
    return (
      <div style={{ padding: '40px', maxWidth: '700px', margin: '0 auto', textAlign: 'center' }}>
        <div style={{ fontSize: '64px', marginBottom: '24px', animation: 'pulse 1.5s infinite' }}>🇪🇺</div>
        <h2 style={{ fontSize: '28px', fontWeight: 'bold', color: '#0f172a', marginBottom: '12px' }}>
          Analyzing your AI system...
        </h2>
        <p style={{ fontSize: '16px', color: '#64748b', marginBottom: '32px' }}>
          Checking against EU AI Act Article 5, Annex III, and all risk categories
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '400px', margin: '0 auto' }}>
          {[
            '🔍 Checking for prohibited practices (Article 5)...',
            '📋 Evaluating Annex III high-risk categories...',
            '⚖️ Assessing transparency obligations...',
            '📊 Generating compliance obligations...'
          ].map((step, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px', backgroundColor: '#f8fafc', borderRadius: '10px', border: '2px solid #e2e8f0', textAlign: 'left' }}>
              <span style={{ fontSize: '14px', color: '#64748b' }}>{step}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ════════════════════════════════
  // RESULTS SCREEN
  // ════════════════════════════════
  if (screen === 'results' && result && level) {
    const obligations = level.obligations || [];
    const urgentObs = obligations.filter(o => o.urgent);
    const normalObs = obligations.filter(o => !o.urgent);

    return (
      <div style={{ padding: '40px', maxWidth: '960px', margin: '0 auto' }}>

        {/* Classification Banner */}
        <div style={{ backgroundColor: level.bg, border: `3px solid ${level.border}`, borderRadius: '20px', padding: '32px', textAlign: 'center', marginBottom: '28px' }}>
          <style>{`
            @keyframes urgency-blink {
              0%, 100% { opacity: 1; box-shadow: 0 0 8px currentColor; }
              50% { opacity: 0.7; box-shadow: none; }
            }
            @keyframes urgent-label-blink {
              0%, 100% { color: #ef4444; }
              50% { color: #fca5a5; }
            }
          `}</style>
          <div style={{ fontSize: '56px', marginBottom: '12px' }}>{level.icon}</div>
          <div style={{ display: 'inline-block', backgroundColor: level.color, color: 'white', fontSize: '12px', fontWeight: 'bold', padding: '4px 14px', borderRadius: '20px', marginBottom: '12px', letterSpacing: '0.05em' }}>
            {level.sublabel}
          </div>
          <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: level.text, margin: '0 0 8px' }}>
            {level.label}
          </h1>
          {companyName && (
            <p style={{ fontSize: '15px', color: level.text, margin: '0 0 6px', opacity: 0.8 }}>{companyName} · {industry}</p>
          )}
          <p style={{ fontSize: '15px', color: level.text, marginBottom: '16px', lineHeight: '1.6', maxWidth: '600px', margin: '0 auto 16px' }}>
            {level.summary}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
            <span style={{ backgroundColor: 'white', color: level.text, padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: `2px solid ${level.border}` }}>
              📋 {level.eu_ref}
            </span>
            {/* Dynamic countdown badge — replaces static enforcement date */}
            {result.classification === 'limited' && (
              <UrgencyBadge dateStr={DEADLINE_AUG2} label="August 2, 2026" />
            )}
            {result.classification === 'high' && (
              <UrgencyBadge dateStr={DEADLINE_DEC2} label="December 2, 2027" />
            )}
            {result.classification === 'unacceptable' && (
              <span style={{ backgroundColor: '#fef2f2', color: '#ef4444', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '800', border: '2px solid #ef4444' }}>
                🚨 Already In Force — Feb 2, 2025
              </span>
            )}
            {result.classification === 'minimal' && (
              <span style={{ backgroundColor: 'white', color: level.text, padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: `2px solid ${level.border}` }}>
                ⏰ {level.enforcement}
              </span>
            )}
            <span style={{ backgroundColor: 'white', color: '#991b1b', padding: '6px 14px', borderRadius: '20px', fontSize: '13px', fontWeight: '600', border: '2px solid #fecaca' }}>
              💰 {level.penalty}
            </span>
          </div>
        </div>

        {/* Prohibited Practice Warning */}
        {result.prohibited_flags?.length > 0 && (
          <div style={{ backgroundColor: '#fef2f2', border: '3px solid #ef4444', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '18px', fontWeight: 'bold', color: '#991b1b', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🚨 Prohibited Practice Flags Detected
            </h3>
            {result.prohibited_flags.map((flag, i) => (
              <p key={i} style={{ fontSize: '14px', color: '#991b1b', margin: '0 0 4px' }}>• {flag}</p>
            ))}
          </div>
        )}

        {/* AI Reasoning + Key Concern */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px', marginBottom: '20px' }}>
          <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 10px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              🤖 Why this classification
              <span style={{ fontSize: '11px', fontWeight: '500', padding: '2px 8px', borderRadius: '10px', backgroundColor: result.confidence === 'high' ? '#dcfce7' : result.confidence === 'medium' ? '#fef3c7' : '#fee2e2', color: result.confidence === 'high' ? '#15803d' : result.confidence === 'medium' ? '#92400e' : '#991b1b' }}>
                {result.confidence?.toUpperCase()} CONFIDENCE
              </span>
            </h3>
            <p style={{ fontSize: '14px', color: '#334155', lineHeight: '1.7', margin: '0 0 10px' }}>
              {result.reasoning}
            </p>
            {result.primary_article && (
              <span style={{ fontSize: '12px', backgroundColor: '#eff6ff', color: '#1e40af', padding: '3px 10px', borderRadius: '10px', fontWeight: '600' }}>
                Primary ref: {result.primary_article}
              </span>
            )}
          </div>
          <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 10px' }}>
              ⚡ Key Concern
            </h3>
            <p style={{ fontSize: '14px', color: '#334155', lineHeight: '1.7', margin: '0 0 16px' }}>
              {result.key_concern}
            </p>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 8px' }}>
              📅 Your Next 30 Days
            </h3>
            <p style={{ fontSize: '14px', color: '#334155', lineHeight: '1.7', margin: 0, fontWeight: '500' }}>
              {result.next_step}
            </p>
          </div>
        </div>

        {/* Top 3 AI-Generated Obligations */}
        {result.top_obligations?.length > 0 && (
          <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
            <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 16px' }}>
              🎯 Top 3 Immediate Actions
            </h2>
            {result.top_obligations.map((ob, i) => (
              <div key={i} style={{ display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '14px', marginBottom: '10px', backgroundColor: i === 0 ? '#fef2f2' : i === 1 ? '#fffbeb' : '#f0fdf4', borderRadius: '10px', border: `2px solid ${i === 0 ? '#fecaca' : i === 1 ? '#fde68a' : '#bbf7d0'}` }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: i === 0 ? '#ef4444' : i === 1 ? '#f59e0b' : '#10b981', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', fontSize: '13px', flexShrink: 0 }}>
                  {i + 1}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontWeight: '600', color: '#0f172a', margin: '0 0 4px', fontSize: '15px' }}>{ob.obligation}</p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>📋 {ob.article}</span>
                    <span style={{ fontSize: '12px', color: '#64748b' }}>⏰ {ob.deadline}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Full Compliance Obligations */}
        <div style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '24px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 16px' }}>
            📋 Full Compliance Obligations for {level.label}
          </h2>
{urgentObs.length > 0 && (() => {
            const limitedStatus = getDeadlineStatus(DEADLINE_AUG2);
            const highStatus = getDeadlineStatus(DEADLINE_DEC2);
            const limitedInForce = result.classification === 'limited' && limitedStatus.status === 'in-force';
            const highInForce = result.classification === 'high' && highStatus.status === 'in-force';

            return (
              <>
                <p style={{
                  fontSize: '13px', fontWeight: '800', margin: '0 0 8px',
                  textTransform: 'uppercase', letterSpacing: '0.05em',
                  animation: (limitedInForce || highInForce) ? 'none' : 'urgent-label-blink 1.2s ease-in-out infinite',
                  color: (limitedInForce || highInForce) ? '#64748b' : '#ef4444',
                  display: 'flex', alignItems: 'center', gap: '8px'
                }}>
                  {(limitedInForce || highInForce) ? '✅ NOW ENFORCED' : '🚨 URGENT'}
                  {result.classification === 'limited' && (
                    <span style={{
                      fontSize: '12px', fontWeight: '700',
                      backgroundColor: limitedInForce ? '#f1f5f9' : '#fef2f2',
                      color: limitedInForce ? '#64748b' : '#ef4444',
                      padding: '2px 10px', borderRadius: '10px',
                      border: `1px solid ${limitedInForce ? '#cbd5e1' : '#fecaca'}`,
                      animation: limitedInForce ? 'none' : 'urgency-blink 1.2s ease-in-out infinite',
                    }}>
                      {limitedInForce ? 'In force since August 2, 2026' : `${limitedStatus.daysUntil} days remaining — August 2, 2026`}
                    </span>
                  )}
                  {result.classification === 'high' && (
                    <span style={{
                      fontSize: '12px', fontWeight: '700',
                      backgroundColor: highInForce ? '#f1f5f9' : '#fffbeb',
                      color: highInForce ? '#64748b' : '#f59e0b',
                      padding: '2px 10px', borderRadius: '10px',
                      border: `1px solid ${highInForce ? '#cbd5e1' : '#fde68a'}`,
                    }}>
                      {highInForce ? 'In force since December 2, 2027' : `${highStatus.daysUntil} days remaining — December 2, 2027`}
                    </span>
                  )}
                </p>
                {urgentObs.map((ob, i) => (
                  <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '12px 14px', marginBottom: '8px', backgroundColor: '#fef2f2', borderRadius: '8px', border: '2px solid #fecaca' }}>
                    <span style={{ fontSize: '16px' }}>🔴</span>
                    <div style={{ flex: 1 }}>
                      <span style={{ fontSize: '14px', fontWeight: '700', color: '#0f172a' }}>{ob.title}</span>
                      <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>{ob.ref}</span>
                      {ob.ref === 'Article 49' && (
                        <span style={{ fontSize: '11px', fontWeight: '700', backgroundColor: '#fef2f2', color: '#ef4444', padding: '1px 8px', borderRadius: '8px', border: '1px solid #fecaca', marginLeft: '8px' }}>
                          EU Database Registration Required
                        </span>
                      )}
                    </div>
                    {result.classification === 'limited' && (
                      <span style={{
                        fontSize: '11px', fontWeight: '700',
                        color: limitedInForce ? '#64748b' : '#ef4444',
                        whiteSpace: 'nowrap',
                        backgroundColor: limitedInForce ? '#f1f5f9' : '#fef2f2',
                        padding: '2px 8px', borderRadius: '8px',
                        border: `1px solid ${limitedInForce ? '#cbd5e1' : '#fecaca'}`,
                        animation: limitedInForce ? 'none' : 'urgency-blink 1.2s ease-in-out infinite',
                      }}>
                        {limitedInForce ? '✅ In force' : `⏰ ${limitedStatus.daysUntil}d left`}
                      </span>
                    )}
                    {result.classification === 'high' && (
                      <span style={{
                        fontSize: '11px', fontWeight: '700',
                        color: highInForce ? '#64748b' : '#f59e0b',
                        whiteSpace: 'nowrap',
                        backgroundColor: highInForce ? '#f1f5f9' : '#fffbeb',
                        padding: '2px 8px', borderRadius: '8px',
                        border: `1px solid ${highInForce ? '#cbd5e1' : '#fde68a'}`,
                      }}>
                        {highInForce ? '✅ In force' : `⏰ ${highStatus.daysUntil}d left`}
                      </span>
                    )}
                  </div>
                ))}
              </>
            );
          })()}

          {normalObs.length > 0 && (
            <>
              <p style={{ fontSize: '13px', fontWeight: '600', color: '#64748b', margin: '12px 0 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Required</p>
              {normalObs.map((ob, i) => (
                <div key={i} style={{ display: 'flex', gap: '12px', alignItems: 'center', padding: '12px 14px', marginBottom: '8px', backgroundColor: '#f8fafc', borderRadius: '8px', border: '2px solid #e2e8f0' }}>
                  <span style={{ fontSize: '16px' }}>🔵</span>
                  <div style={{ flex: 1 }}>
                    <span style={{ fontSize: '14px', color: '#334155' }}>{ob.title}</span>
                    <span style={{ fontSize: '12px', color: '#64748b', marginLeft: '8px' }}>{ob.ref}</span>
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Similar Examples */}
        {result.similar_examples?.length > 0 && (
          <div style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#0f172a', margin: '0 0 12px' }}>
              💡 Similar AI Systems in This Category
            </h3>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
              {result.similar_examples.map((ex, i) => (
                <span key={i} style={{ fontSize: '13px', padding: '6px 12px', backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '20px', color: '#334155' }}>
                  {ex}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Fines Reference */}
        <div style={{ backgroundColor: '#fef2f2', border: '2px solid #fecaca', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
          <h3 style={{ fontSize: '16px', fontWeight: 'bold', color: '#991b1b', margin: '0 0 12px' }}>
            💰 EU AI Act Fines Reference
          </h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '10px' }}>
            {classifierData.fines.map((fine, i) => (
              <div key={i} style={{ backgroundColor: 'white', borderRadius: '8px', padding: '12px', border: '2px solid #fecaca' }}>
                <p style={{ fontSize: '11px', fontWeight: '700', color: '#ef4444', margin: '0 0 4px', textTransform: 'uppercase' }}>Tier {fine.tier}</p>
                <p style={{ fontSize: '13px', color: '#334155', margin: '0 0 4px', fontWeight: '500' }}>{fine.violation}</p>
                <p style={{ fontSize: '13px', color: '#991b1b', margin: 0, fontWeight: '700' }}>{fine.fine}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Disclaimer */}
        <div style={{ padding: '14px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px', marginBottom: '24px' }}>
          <p style={{ margin: 0, color: '#92400e', fontSize: '13px' }}>
            <strong>⚠️ Disclaimer:</strong> {classifierData.meta.disclaimer}
          </p>
        </div>

        {/* Action Buttons */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={downloadPDFReport}
            disabled={pdfGenerating}
            style={{
              flex: 1, padding: '14px',
              backgroundColor: pdfGenerating ? '#cbd5e1' : '#3b82f6',
              color: 'white', border: 'none', borderRadius: '10px',
              fontSize: '16px', fontWeight: 'bold',
              cursor: pdfGenerating ? 'wait' : 'pointer'
            }}
          >
            {pdfGenerating ? '⏳ Generating PDF...' : '⬇️ Download as PDF'}
          </button>
        </div>

        {/* ══════════════════════════════ */}
        {/* POST-CLASSIFICATION ACTION PLAN */}
        {/* ══════════════════════════════ */}
        <div style={{ backgroundColor: '#eff6ff', border: '3px solid #3b82f6', borderRadius: '16px', padding: '28px', marginBottom: '20px' }}>
          <h2 style={{ fontSize: '20px', fontWeight: 'bold', color: '#1e40af', margin: '0 0 8px' }}>
            🗺️ Your EU AI Act Readiness Action Plan
          </h2>
          <p style={{ fontSize: '14px', color: '#64748b', margin: '0 0 20px', lineHeight: '1.6' }}>
            Based on your classification result, here are your next steps — in order. Each links to the tool in this platform that handles it.
          </p>

          {[
            {
              step: 1, icon: '⚖️', title: 'Complete Risk Assessment',
              desc: 'Run the multi-framework risk calculator to get your detailed risk profile across EU AI Act, ISO 42001, and NIST AI RMF.',
              action: 'risk', actionLabel: '→ Run Risk Assessment',
              article: 'Article 9 — Risk Management System',
              done: !!localStorage.getItem(userKey('qais_risk_result')),
            },
            {
              step: 2, icon: '✏️', title: 'Complete Compliance Checklist',
              desc: 'Work through your personalized compliance checklist — every EU AI Act obligation tracked with priority and deadline.',
              action: 'checklist', actionLabel: '→ Open Checklist',
              article: 'Articles 9–15 — All High-Risk Obligations',
              done: !!localStorage.getItem(userKey('qais_checklist_setup')),
            },
            {
              step: 3, icon: '📋', title: 'Generate AI Policy',
              desc: 'Auto-generate your AI Policy document tailored to your organisation, industry, and compliance framework.',
              action: 'documents', actionLabel: '→ Generate Documents',
              article: 'Article 11 — Technical Documentation',
              done: parseInt(localStorage.getItem(userKey('qais_docs_generated')) || '0') > 0,
            },
            {
              step: 4, icon: '📢', title: 'Generate Transparency Disclosure',
              desc: 'Create your Article 50 transparency disclosure — required by August 2, 2026 for all AI systems interacting with EU individuals.',
              action: 'documents', actionLabel: '→ Generate Transparency Doc',
              article: 'Article 50 — Transparency Obligations',
              done: false,
            },
            {
              step: 5, icon: '🔐', title: 'Record AI Decisions on Quantum-Safe Ledger',
              desc: 'Start logging AI decisions to the blockchain audit trail. Every record is ML-DSA-65 signed and tamper-evident — required for Article 12 event logging.',
              action: 'dashboard', actionLabel: '→ AI Decisions Tab',
              article: 'Article 12 — Record-Keeping',
              done: false,
            },
            {
              step: 6, icon: '🔗', title: 'Map Across Frameworks',
              desc: 'Generate a unified compliance document that satisfies ISO 42001, EU AI Act, and NIST AI RMF simultaneously.',
              action: 'mapper', actionLabel: '→ Cross-Framework Mapper',
              article: 'Multi-Framework Alignment',
              done: false,
            },
          ].map(item => (
            <div key={item.step} style={{
              display: 'flex', gap: '16px', alignItems: 'flex-start',
              padding: '16px', marginBottom: '10px', borderRadius: '12px',
              backgroundColor: item.done ? '#f0fdf4' : 'white',
              border: `2px solid ${item.done ? '#86efac' : '#e2e8f0'}`,
            }}>
              <div style={{
                width: '36px', height: '36px', borderRadius: '50%', flexShrink: 0,
                backgroundColor: item.done ? '#10b981' : '#3b82f6',
                color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '14px', fontWeight: 'bold'
              }}>
                {item.done ? '✓' : item.step}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
                  <span style={{ fontSize: '15px', fontWeight: '700', color: '#0f172a' }}>{item.icon} {item.title}</span>
                  <span style={{ fontSize: '11px', padding: '2px 8px', borderRadius: '10px',
                    backgroundColor: item.done ? '#dcfce7' : '#eff6ff',
                    color: item.done ? '#166534' : '#1e40af',
                    fontWeight: '600' }}>
                    {item.done ? '✓ Done' : item.article}
                  </span>
                </div>
                <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 8px', lineHeight: '1.5' }}>{item.desc}</p>
                {!item.done && onNavigate && (
                  <button
                    onClick={() => onNavigate(item.action)}
                    style={{ padding: '8px 16px', backgroundColor: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}
                  >
                    {item.actionLabel}
                  </button>
                )}
              </div>
            </div>
          ))}

          {/* Art. 16 Conformity Assessment + Art. 49 EU Database — guidance only */}
          <div style={{ backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '12px', padding: '16px', marginTop: '16px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 'bold', color: '#92400e', margin: '0 0 10px' }}>
              📌 Additional Requirements — External Actions Needed
            </h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <span style={{ fontWeight: '700', color: '#92400e', flexShrink: 0 }}>Art. 16:</span>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                  <strong>Conformity Assessment</strong> — Before placing a high-risk AI system on the EU market, you must conduct a conformity assessment. For Annex III standalone systems, self-assessment is permitted for most categories. For biometric systems, a notified body assessment is required. Deadline: December 2, 2027 for standalone systems.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <span style={{ fontWeight: '700', color: '#92400e', flexShrink: 0 }}>Art. 49:</span>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                  <strong>EU AI Database Registration</strong> — High-risk AI systems must be registered in the EU public database before deployment. Registration includes system purpose, provider details, conformity status, and risk classification. Register at: <strong>aida.ec.europa.eu</strong> when available.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
                <span style={{ fontWeight: '700', color: '#92400e', flexShrink: 0 }}>Art. 22:</span>
                <p style={{ fontSize: '13px', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                  <strong>EU Authorised Representative</strong> — If your organisation is outside the EU, you must designate an EU-based authorised representative before placing a high-risk AI system on the EU market.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          <button
            onClick={handleReset}
            style={{ flex: 1, padding: '14px', backgroundColor: 'white', color: '#334155', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '16px', fontWeight: 'bold', cursor: 'pointer', minWidth: '160px' }}
          >
            🔄 Re-run Classification
          </button>
          <button
            onClick={handleResetNexus}
            style={{ flex: 1, padding: '14px', backgroundColor: 'white', color: '#64748b', border: '2px solid #e2e8f0', borderRadius: '10px', fontSize: '14px', fontWeight: '600', cursor: 'pointer', minWidth: '160px' }}
          >
            ✏️ Re-check Jurisdiction
          </button>
        </div>
      </div>
    );
  }

  // ════════════════════════════════
  // INTRO / INPUT SCREEN
  // ════════════════════════════════
  return (
    <div style={{ padding: '40px', maxWidth: '800px', margin: '0 auto' }}>

      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '24px' }}>
        <div style={{ fontSize: '56px', marginBottom: '14px' }}>🇪🇺</div>
        <h1 style={{ fontSize: '36px', fontWeight: 'bold', color: '#0f172a', marginBottom: '10px' }}>
          EU AI Act Classifier
        </h1>
        <p style={{ fontSize: '22px', color: '#64748b', marginBottom: '16px' }}>
          Describe your AI system and get an instant risk classification
        </p>
        <div style={{ display: 'flex', gap: '40px', justifyContent: 'center', flexWrap: 'wrap' }}>
          {[
            { icon: '🚫', label: 'Unacceptable', color: '#ef4444' },
            { icon: '⚠️', label: 'High Risk', color: '#f59e0b' },
            { icon: 'ℹ️', label: 'Limited Risk', color: '#3b82f6' },
            { icon: '✅', label: 'Minimal Risk', color: '#10b981' },
          ].map(item => (
            <span key={item.label} style={{ fontSize: '13px', padding: '6px 14px', borderRadius: '20px', backgroundColor: 'white', border: `2px solid ${item.color}`, color: item.color, fontWeight: '600' }}>
              {item.icon} {item.label}
            </span>
          ))}
        </div>
      </div>

      {/* CARD 1: Applicability rules — ALWAYS shown */}
      <div style={{ backgroundColor: '#eff6ff', border: '2px solid #93c5fd', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
        <p style={{ fontSize: '13px', fontWeight: '700', color: '#1e3a8a', margin: '0 0 12px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          📌 BEFORE YOU START — When does EU AI Act apply?
        </p>
        <p style={{ fontSize: '14px', color: '#1e40af', margin: '0 0 12px', lineHeight: '1.6' }}>
          Your AI is in scope of EU AI Act if <strong>any</strong> of these is true:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '12px' }}>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '15px', color: '#1e40af', fontWeight: '700' }}>✓</span>
            <p style={{ fontSize: '13px', color: '#1e40af', margin: 0, lineHeight: '1.6' }}>
              <strong>Provider</strong> — you build/sell AI placed on the EU market or put into service in the EU
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '15px', color: '#1e40af', fontWeight: '700' }}>✓</span>
            <p style={{ fontSize: '13px', color: '#1e40af', margin: 0, lineHeight: '1.6' }}>
              <strong>Deployer</strong> — you're located in the EU and use AI
            </p>
          </div>
          <div style={{ display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <span style={{ fontSize: '15px', color: '#1e40af', fontWeight: '700' }}>✓</span>
            <p style={{ fontSize: '13px', color: '#1e40af', margin: 0, lineHeight: '1.6' }}>
              <strong>Output reach</strong> — your AI's output is used in the EU, even if you're outside the EU
            </p>
          </div>
        </div>
        <p style={{ fontSize: '12px', color: '#7c2d12', margin: 0, fontStyle: 'italic', backgroundColor: '#fef3c7', padding: '8px 12px', borderRadius: '6px', border: '1px solid #fbbf24' }}>
          ⚠️ Most companies miss the third one — a single EU customer = in scope.
        </p>
      </div>

      {/* CARD 2 removed — nexus gate now handles EU scope routing before reaching this screen */}

      {/* CARD 3: Risk Score not yet completed banner */}
      {!hasRiskScore && (
        <div style={{ backgroundColor: '#f0f9ff', border: '2px solid #7dd3fc', borderRadius: '12px', padding: '16px 20px', marginBottom: '20px', display: 'flex', gap: '14px', alignItems: 'flex-start' }}>
          <span style={{ fontSize: '24px', flexShrink: 0 }}>💡</span>
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: '14px', color: '#075985', margin: '0 0 6px', fontWeight: '700' }}>
              Complete Risk Score first for richer classification
            </p>
            <p style={{ fontSize: '13px', color: '#0c4a6e', margin: 0, lineHeight: '1.6' }}>
              Running the 12-question Risk Score assessment first gives our AI more signal — your NIST concern level, ISO readiness, governance maturity, and oversight measures all sharpen the EU AI Act classification. You can still proceed without it.
            </p>
          </div>
          {onNavigate && (
            <button
              onClick={() => onNavigate('risk')}
              style={{ padding: '10px 16px', backgroundColor: '#0284c7', color: 'white', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '700', cursor: 'pointer', flexShrink: 0 }}
            >
              Go to Risk Score
            </button>
          )}
        </div>
      )}

      {/* Input Form */}
      <div id="eu-classifier-form" style={{ backgroundColor: 'white', border: '2px solid #e2e8f0', borderRadius: '16px', padding: '32px', marginBottom: '24px' }}>

        {/* Company Name */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '8px', fontSize: '15px' }}>
            Company Name {companyLocked && <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '500' }}>🔒 from your profile</span>}
          </label>
          <input
            type="text"
            value={companyName}
            onChange={e => !companyLocked && setCompanyName(e.target.value)}
            readOnly={companyLocked}
            placeholder="e.g. Acme AI Solutions"
            style={{ width: '100%', padding: '12px 14px', fontSize: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', boxSizing: 'border-box', backgroundColor: companyLocked ? '#f8fafc' : 'white', color: companyLocked ? '#64748b' : '#0f172a', cursor: companyLocked ? 'not-allowed' : 'text' }}
          />
        </div>

        {/* Industry */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '8px', fontSize: '15px' }}>
            Industry <span style={{ color: '#ef4444' }}>*</span> {industryLocked && <span style={{ fontSize: '12px', color: '#10b981', fontWeight: '500' }}>🔒 from your profile</span>}
          </label>
          {industryLocked ? (
            <input
              type="text"
              value={industry}
              readOnly
              style={{ width: '100%', padding: '12px 14px', fontSize: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', backgroundColor: '#f8fafc', color: '#64748b', boxSizing: 'border-box', cursor: 'not-allowed' }}
            />
          ) : (
            <select
              value={industry}
              onChange={e => setIndustry(e.target.value)}
              style={{ width: '100%', padding: '12px 14px', fontSize: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', backgroundColor: 'white', boxSizing: 'border-box', cursor: 'pointer' }}
            >
              <option value="">Select your industry...</option>
              {industries.map(ind => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
          )}
        </div>

        {/* AI System Description */}
        <div style={{ marginBottom: '8px' }}>
          <label style={{ display: 'block', fontWeight: '600', color: '#334155', marginBottom: '8px', fontSize: '15px' }}>
            Describe your AI system in plain English <span style={{ color: '#ef4444' }}>*</span>
          </label>
          <p style={{ fontSize: '13px', color: '#64748b', margin: '0 0 10px' }}>
            💡 Be specific — what does it do, who uses it, what decisions does it influence, what data does it process?
          </p>
          <textarea
            value={systemDescription}
            onChange={e => { setSystemDescription(e.target.value); setCharCount(e.target.value.length); }}
            placeholder="e.g. We use an AI system to screen job applications. It reads CVs and ranks candidates based on skills and experience match, shortlisting the top 20% for human review. It processes personal data including name, work history, and education."
            rows={6}
            style={{ width: '100%', padding: '14px', fontSize: '15px', border: '2px solid #e2e8f0', borderRadius: '10px', outline: 'none', resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: '1.6' }}
          />
          <p style={{ textAlign: 'right', fontSize: '12px', color: charCount < 50 ? '#ef4444' : '#94a3b8', margin: '4px 0 0' }}>
            {charCount} characters {charCount < 50 ? '— please add more detail (min 50)' : '✓'}
          </p>

          {descriptionDiffers && (
            <div style={{ marginTop: '10px', padding: '10px 14px', backgroundColor: '#fef3c7', border: '1px solid #fde68a', borderRadius: '8px' }}>
              <p style={{ fontSize: '12px', color: '#92400e', margin: 0, lineHeight: '1.5' }}>
                📝 Note: Your description here differs from your profile's stored AI system purpose. That's fine — this classification will use what you've entered above. Update your profile if you want them to match.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Examples */}
      <div style={{ backgroundColor: '#f8fafc', border: '2px solid #e2e8f0', borderRadius: '12px', padding: '20px', marginBottom: '24px' }}>
        <p style={{ fontWeight: '600', color: '#334155', marginBottom: '12px', fontSize: '14px' }}>
          💡 Example descriptions that work well:
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            '"An AI chatbot on our website that answers customer questions about products and services."',
            '"A machine learning model that evaluates credit applications and recommends approval or rejection."',
            '"An AI tool that screens job applicants by analyzing CVs and scoring candidates."',
            '"A recommendation engine that suggests products to customers based on their browsing history."',
          ].map((ex, i) => (
            <div
              key={i}
              onClick={() => { setSystemDescription(ex.replace(/^"|"$/g, '')); setCharCount(ex.length - 2); }}
              style={{ padding: '10px 14px', backgroundColor: 'white', borderRadius: '8px', border: '2px solid #e2e8f0', cursor: 'pointer', fontSize: '13px', color: '#334155', lineHeight: '1.5' }}
              onMouseEnter={e => e.currentTarget.style.borderColor = '#3b82f6'}
              onMouseLeave={e => e.currentTarget.style.borderColor = '#e2e8f0'}
            >
              {ex} <span style={{ color: '#3b82f6', fontSize: '12px' }}>← click to use</span>
            </div>
          ))}
        </div>
      </div>

      {/* Classify Button */}
      <button
        onClick={classifySystem}
        disabled={!systemDescription.trim() || systemDescription.length < 50 || !industry || loading}
        style={{
          width: '100%',
          padding: '18px',
          backgroundColor: !systemDescription.trim() || systemDescription.length < 50 || !industry ? '#cbd5e1' : '#3b82f6',
          color: 'white',
          border: 'none',
          borderRadius: '12px',
          fontSize: '18px',
          fontWeight: 'bold',
          cursor: !systemDescription.trim() || systemDescription.length < 50 || !industry ? 'not-allowed' : 'pointer',
          transition: 'background-color 0.2s',
          marginBottom: '16px'
        }}
      >
        🇪🇺 Classify My AI System
      </button>

      {/* Disclaimer */}
      <div style={{ padding: '14px', backgroundColor: '#fef3c7', border: '2px solid #fbbf24', borderRadius: '10px' }}>
        <p style={{ margin: 0, color: '#92400e', fontSize: '13px' }}>
          <strong>⚠️ Disclaimer:</strong> {classifierData.meta.disclaimer}
        </p>
      </div>
    </div>
  );
}

export default EUAIActClassifier;